import React from "react";
import ImageUploader from "react-images-upload";
import { storage } from "../../utils/firebase";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import ImagesPreview from "./ImagesPreview";
import { useIntl } from "react-intl";
import { PreviewPhoto } from "../../types/siteTypes";
import { connect } from "react-redux";
import { IAuth } from "../../types/authTypes";
import { authSelector } from "../../utils/selectors";
import styles from "./UploadPhoto.module.scss";

type InnerProps = {
  auth: IAuth;
};

type Props = {
  photos: PreviewPhoto[];
  updatePhotoList: (photos: PreviewPhoto[]) => void;
  onReorder?: (photos: PreviewPhoto[]) => void;
} & InnerProps;

const UploadPhoto: React.FC<Props> = ({
  photos,
  updatePhotoList,
  auth,
}) => {
  const intl = useIntl();
  const [loading, setLoading] = React.useState(0);
  const [processingMessage, setProcessingMessage] = React.useState<string>("");
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);

  const getDownloadUrlSafe = async (path: string): Promise<string | null> => {
    try {
      return await getDownloadURL(ref(storage, path));
    } catch (err: any) {
      if (err.code === "storage/object-not-found" || err.code === "storage/unauthorized") {
        return null;
      }
      throw err;
    }
  };

  const getResizedCandidates = (basePath: string, filename: string) => {
    const ext = filename.substring(filename.lastIndexOf("."));
    const name = filename.substring(0, filename.lastIndexOf("."));
    return {
      main: [
        `${basePath}/${name}_1200x1200${ext}`,
        `${basePath}/${name}_1200x1200.webp`,
      ],
      thumb: [
        `${basePath}/${name}_200x200${ext}`,
        `${basePath}/${name}_200x200.webp`,
      ],
      original: `${basePath}/${filename}`,
    };
  };

  const pollForResizedImage = async (
    basePath: string,
    filename: string,
    maxAttempts: number = 15
  ): Promise<{ mainUrl: string; thumbUrl: string } | null> => {
    const candidates = getResizedCandidates(basePath, filename);

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      let mainUrl = null;
      let thumbUrl = null;

      for (const p of candidates.main) {
        mainUrl = await getDownloadUrlSafe(p);
        if (mainUrl) break;
      }

      for (const p of candidates.thumb) {
        thumbUrl = await getDownloadUrlSafe(p);
        if (thumbUrl) break;
      }

      if (mainUrl && thumbUrl) {
        return { mainUrl, thumbUrl };
      }

      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    // Last fallback: try to use original (if not deleted)
    const originalUrl = await getDownloadUrlSafe(candidates.original);
    if (originalUrl) {
      return { mainUrl: originalUrl, thumbUrl: originalUrl };
    }

    return null;
  };

  const onDrop = (images: any) => {
    if (isProcessing) {
      return; // Prevent new uploads while processing
    }

    setIsProcessing(true);

    images.map((photo: any) => {
      const storageRef = ref(
        storage,
        `images/${auth.user && auth.user._id}/${photo.name}`
      );
      const uploadTask = uploadBytesResumable(storageRef, photo);

      return uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setLoading(progress);
          setProcessingMessage(`Uploading... ${progress}%`);
        },
        (error) => {
          console.log(error.message);
          setLoading(100);
          setProcessingMessage("");
          setIsProcessing(false);
        },
        async () => {
          setProcessingMessage("Processing images...");

          const basePath = `images/${auth.user && auth.user._id}`;
          const resizedUrls = await pollForResizedImage(basePath, photo.name);

          if (resizedUrls) {
            updatePhotoList(
              photos.concat({
                url: resizedUrls.mainUrl,
                thumbnailUrl: resizedUrls.thumbUrl,
                name: photo.name,
                processing: false,
              })
            );
          } else {
            // Fallback to original if resizing failed
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            updatePhotoList(
              photos.concat({ url, name: photo.name, processing: false })
            );
          }

          setLoading(100);
          setIsProcessing(false);
          setProcessingMessage("");
        }
      );
    });
  };

  const onDelete = async (photo: PreviewPhoto) => {
    const basePath = `images/${auth.user && auth.user._id}`;
    const nameWithoutExt = photo.name.substring(0, photo.name.lastIndexOf("."));

    // Delete both resized versions (original is already deleted by extension)
    const mainImageRef = ref(storage, `${basePath}/${nameWithoutExt}_1200x1200.webp`);
    const thumbImageRef = ref(storage, `${basePath}/${nameWithoutExt}_200x200.webp`);
    const originalRef = ref(storage, `${basePath}/${photo.name}`);

    try {
      // Try to delete all possible versions
      await Promise.allSettled([
        deleteObject(mainImageRef),
        deleteObject(thumbImageRef),
        deleteObject(originalRef), // In case extension hasn't processed yet
      ]);
      updatePhotoList(photos.filter((pix) => pix !== photo));
    } catch (err: any) {
      console.log(err.message);
      // Still remove from list even if deletion fails
      updatePhotoList(photos.filter((pix) => pix !== photo));
    }
  };
  return (
    <>
      <ImageUploader
        withIcon={false}
        buttonText={intl.formatMessage({
          id: "site.imageUpload.chooseImage",
          defaultMessage: "Choose image",
        })}
        onChange={onDrop}
        imgExtension={[".jpg", ".gif", ".png", ".jpeg", ".webp"]}
        maxFileSize={12242880}
        singleImage={true}
        label={intl.formatMessage({
          id: "site.imageUpload.uploadLabel",
          defaultMessage: "Max file size: 12mb, accepted: jpg | gif | png | jpeg | webp",
        })}
        fileSizeError={intl.formatMessage({
          id: "site.imageUpload.fileSizeTooBig",
          defaultMessage: "File size is too big",
        })}
      />
      {processingMessage && (
        <div className={`${styles.processingMessage} ${isProcessing ? styles.processing : styles.idle}`}>
          {processingMessage}
        </div>
      )}
      <ImagesPreview
        photos={photos}
        onDelete={onDelete}
        loading={loading}
        onReorder={updatePhotoList}
      />
    </>
  );
};

const mapState = (state: InnerProps) => ({
  auth: authSelector(state),
});

export default connect(mapState)(UploadPhoto);
